import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { ParamsTimeType } from '@op/types/common';
import { getRealXAxisValue } from '@op/utils/chart';
import type { AnchorMode, IEventItem, IHighPriorityEventItem } from '../types';

const SERIES_NAME = 'TopTipsEvent';

interface MarkAreaState {
  renderMarkArea: { width: string; left: string; height?: string; top?: string };
  eventLineShow: boolean;
  markAreaShow: boolean;
  markAreaTimes: string;
}

interface UseEventItemPositionProps {
  myChart: any;
  xAxisValue: string;
  format: string;
  dateType: ParamsTimeType;
  xAxisData: Array<string>;
  yAxisMin?: boolean;
  backPositionInfo: (item: {
    eventLine: any;
    barLeftPosition: number;
    barBottomPosition: number;
    eventBox: any;
    anchorMode?: AnchorMode;
    shouldHide?: boolean;
  }) => void;
  anchorMode?: AnchorMode;
  event?: IEventItem | IHighPriorityEventItem;
}

export function useEventItemPosition({
  myChart,
  xAxisValue,
  format,
  dateType,
  xAxisData,
  backPositionInfo,
  yAxisMin = false,
  anchorMode = 'start',
  event,
}: UseEventItemPositionProps) {
  const eventBoxRef = useRef<any>(null);
  const eventLineRef = useRef<any>(null);
  const currentItemPosition = useRef<number>(0);

  // 合并多个独立状态为单一对象，减少重渲染次数
  const [markAreaState, setMarkAreaState] = useState<MarkAreaState>({
    renderMarkArea: { width: '0px', left: '0px' },
    eventLineShow: false,
    markAreaShow: false,
    markAreaTimes: '',
  });

  /**
   * 校验时间格式是否正确
   */
  const validateTimeFormat = (time: string): boolean => {
    return moment(time, format, true).isValid();
  };

  /**
   * 校验时间范围是否有效（endTime >= startTime）
   */
  const validateTimeRange = (startTime: string, end: string): boolean => {
    return moment(end).isSameOrAfter(moment(startTime));
  };

  /**
   * 检查时间是否在 X 轴数据范围内，并返回实际的时间轴值
   */
  const getTimeInXAxisData = (time: string): { isInAxis: boolean; realValue: string } => {
    if (!xAxisData || xAxisData.length === 0) {
      return { isInAxis: false, realValue: time };
    }
    const realXAxisValue = getRealXAxisValue({
      xAxis: time,
      dateType,
      format,
      xAxisData,
    });
    return { isInAxis: xAxisData.includes(realXAxisValue), realValue: realXAxisValue };
  };

  /**
   * 开发环境日志输出
   */
  const devWarn = (message: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[ChartsEvents] ${message}`);
    }
  };

  /**
   * 验证锚点时间配置
   * @returns anchorTime, actualAnchorMode, shouldHide
   */
  const validateAnchorTime = (): {
    anchorTime: string;
    actualAnchorMode: AnchorMode;
    shouldHide: boolean;
  } => {
    // 从 event 中提取时间（仅高优先级事件使用）
    const startTime = event?.start_time;
    const endTime = event?.end_time;

    // xAxisValue 是事件的定位点，必须存在于时间轴上
    const xAxisResult = getTimeInXAxisData(xAxisValue);
    if (!xAxisResult.isInAxis) {
      // 降级优化：仅高优先级事件（有 event）检查 startTime 是否在时间轴上
      if (event && startTime) {
        const startResult = getTimeInXAxisData(startTime);
        if (startResult.isInAxis) {
          devWarn(`xAxisValue (${xAxisValue}) is not in xAxisData, falling back to startTime (${startResult.realValue})`);
          return { anchorTime: startResult.realValue, actualAnchorMode: 'start', shouldHide: false };
        }
      }
      devWarn(`xAxisValue (${xAxisValue}) is not in xAxisData, hiding event`);
      return { anchorTime: xAxisValue, actualAnchorMode: 'start', shouldHide: true };
    }

    // 非 end 模式，直接使用 xAxisValue
    if (anchorMode !== 'end') {
      return { anchorTime: xAxisResult.realValue, actualAnchorMode: anchorMode, shouldHide: false };
    }

    // end 模式但无 endTime，降级为 start
    if (!endTime) {
      devWarn('anchorMode is "end" but no endTime provided, falling back to start_time');
      return { anchorTime: xAxisResult.realValue, actualAnchorMode: 'start', shouldHide: false };
    }

    // endTime 格式无效，降级为 start
    if (!validateTimeFormat(endTime)) {
      devWarn(`Invalid endTime format: ${endTime}, expected: ${format}`);
      return { anchorTime: xAxisResult.realValue, actualAnchorMode: 'start', shouldHide: false };
    }

    // endTime 早于 startTime，降级为 start
    if (startTime && !validateTimeRange(startTime, endTime)) {
      devWarn(`endTime (${endTime}) is before startTime (${startTime}), falling back to start_time`);
      return { anchorTime: xAxisResult.realValue, actualAnchorMode: 'start', shouldHide: false };
    }

    // endTime 不在 X 轴数据范围内，隐藏事件
    const endResult = getTimeInXAxisData(endTime);
    if (!endResult.isInAxis) {
      devWarn(`endTime (${endTime}) is not in xAxisData, hiding event`);
      return { anchorTime: xAxisResult.realValue, actualAnchorMode: 'start', shouldHide: true };
    }

    return { anchorTime: endResult.realValue, actualAnchorMode: 'end', shouldHide: false };
  };

  function getBarLeftPosition({
    seriesName = SERIES_NAME,
    xAxis = xAxisValue,
  }: {
    seriesName?: string;
    xAxis?: string;
  } = {}) {
    const realXAxisValue = getRealXAxisValue({
      xAxis,
      dateType,
      format,
      xAxisData,
    });

    try {
      // 获取 Y 轴的配置
      const yAxisModel = myChart.getModel().getComponent('yAxis', 0);
      let yValue = 0;
      if (yAxisModel && yAxisMin) {
        const { scale } = yAxisModel.axis;
        const extent = scale.getExtent();
        // eslint-disable-next-line prefer-destructuring
        yValue = extent[0];
      }

      const bottomPixelPosition = myChart.convertToPixel({ seriesName }, [
        realXAxisValue,
        yValue,
      ]);

      return { left: bottomPixelPosition[0], bottom: bottomPixelPosition[1] };
    } catch (error) {
      return { left: 0, bottom: 0 };
    }
  }

  function setEventBoxPosition({
    seriesName = SERIES_NAME,
    callback = true,
  }: { seriesName?: string; callback?: boolean } = {}) {
    const { anchorTime, actualAnchorMode, shouldHide } = validateAnchorTime();
    const leftPosition = getBarLeftPosition({
      seriesName,
      xAxis: anchorTime,
    });

    currentItemPosition.current = leftPosition.left;
    if (callback && eventBoxRef.current) {
      backPositionInfo({
        barLeftPosition: leftPosition.left,
        barBottomPosition: leftPosition.bottom,
        eventBox: eventBoxRef.current,
        eventLine: eventLineRef.current,
        anchorMode: actualAnchorMode,
        shouldHide,
      });
    }
  }

  useEffect(() => {
    if (myChart && xAxisData && xAxisData.length > 0) {
      setEventBoxPosition();
    }
  }, [myChart, xAxisData, xAxisValue, anchorMode]);

  const setRenderMarkArea = (value: MarkAreaState['renderMarkArea']) =>
    setMarkAreaState((prev) => ({ ...prev, renderMarkArea: value }));

  const setEventLineShow = (value: boolean) =>
    setMarkAreaState((prev) => ({ ...prev, eventLineShow: value }));

  const setMarkAreaShow = (value: boolean) =>
    setMarkAreaState((prev) => ({ ...prev, markAreaShow: value }));

  const setMarkAreaTimes = (value: string) =>
    setMarkAreaState((prev) => ({ ...prev, markAreaTimes: value }));

  return {
    eventBoxRef,
    eventLineRef,
    currentItemPosition,
    renderMarkArea: markAreaState.renderMarkArea,
    eventLineShow: markAreaState.eventLineShow,
    markAreaShow: markAreaState.markAreaShow,
    markAreaTimes: markAreaState.markAreaTimes,
    setRenderMarkArea,
    setEventLineShow,
    setMarkAreaShow,
    setMarkAreaTimes,
    setMarkAreaState,
    getBarLeftPosition,
    setEventBoxPosition,
  };
}
